import { Button } from "@/components/ui/button";
import { Check, ArrowRight } from "lucide-react";

interface Package {
  name: string;
  originalPrice?: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
  orderLink?: string;
}

interface PricingTableProps {
  packages: Package[];
  title: string;
  subtitle: string;
}

const PricingTable = ({ packages, title, subtitle }: PricingTableProps) => {
  return (
    <section className="py-20 bg-background">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.name}
              className={`relative p-8 rounded-2xl bg-card border ${
                pkg.popular ? "border-primary shadow-glow" : "border-border"
              } card-hover`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="gradient-primary text-primary-foreground text-sm font-medium px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {pkg.name}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {pkg.description}
                </p>
              <div className="flex flex-col items-center gap-1">
                  {pkg.originalPrice && (
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-sm text-muted-foreground line-through">৳{pkg.originalPrice}</span>
                    </div>
                  )}
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-sm text-muted-foreground">৳</span>
                    <span className="text-4xl font-bold text-foreground">{pkg.price}</span>
                    <span className="text-muted-foreground">{pkg.period}</span>
                  </div>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {pkg.orderLink ? (
                <Button
                  variant={pkg.popular ? "gradient" : "outline"}
                  className="w-full"
                  asChild
                >
                  <a href={pkg.orderLink}>
                    Order Now
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              ) : (
                <Button
                  variant={pkg.popular ? "gradient" : "outline"}
                  className="w-full"
                >
                  Order Now
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingTable;
